package com.patronat.top.features.dashboard.presentation.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.patronat.top.core.domain.models.profiles.User
import com.patronat.top.core.navigation.DashboardSection
import com.patronat.top.dto.DashboardStatsResponse
import com.patronat.top.dto.UserRole
import com.patronat.top.features.dashboard.presentation.MainComponent
import com.patronat.top.features.dashboard.presentation.contract.DashboardContract
import com.patronat.top.features.dashboard.presentation.ownerreviews.OwnerReviewsScreen
import com.patronat.top.features.dashboard.presentation.reviewmoderation.ReviewAiModerationScreen
import com.patronat.top.features.dashboard.presentation.viewmodel.DashboardViewModel
import com.patronat.top.features.usermanagement.presentation.UsersScreen
import com.patronat.top.features.venues.presentation.VenuePickedFile
import com.patronat.top.features.venues.presentation.VenuesScreen
import com.patronat.top.ui.components.organisms.AdminPageHeader
import com.patronat.top.ui.models.NavigationUiItem
import com.patronat.top.ui.organisms.EmptyStateContent
import com.patronat.top.ui.organisms.SideNavigationPanel
import com.patronat.top.ui.theme.AppIcons
import com.patronat.top.ui.theme.AppIcons.NavAiModeration
import com.patronat.top.ui.theme.AppIcons.NavBusiness
import com.patronat.top.ui.theme.AppIcons.NavDashboard
import com.patronat.top.ui.theme.AppIcons.NavModerators
import com.patronat.top.ui.theme.AppIcons.NavReview
import com.patronat.top.ui.theme.AppIcons.NavSettings
import com.patronat.top.ui.theme.AppIcons.NavUsers
import com.patronat.top.ui.theme.White
import kotlinx.browser.document
import kotlinx.coroutines.await
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File
import com.patronat.top.library.media.data.KmpFileSource
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import top.features.dashboard.generated.resources.Res
import top.features.dashboard.generated.resources.*
import kotlin.js.Promise

@Composable
actual fun MainScreen(component: MainComponent, userRole: UserRole) {
    val viewModel = koinInject<DashboardViewModel>()
    val state by viewModel.viewState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is DashboardContract.Effect.NavigateToAuth -> {
                    component.onLogoutClicked()
                }
            }
        }
    }
    if (state.isLoading) {
        Box(
            modifier = Modifier.fillMaxSize().background(White),
            contentAlignment = Alignment.Center
        ) {
            CircularProgressIndicator()
        }
    } else {
        state.user?.let { user ->
            DashboardLayout(
                user = user,
                currentSection = state.currentSection,
                dashboardStats = state.dashboardStats,
                isStatsLoading = state.isStatsLoading,
                onEvent = viewModel::onEvent
            )
        } ?: run {
            ErrorScreen(onRetry = { viewModel.onEvent(DashboardContract.Event.OnRetryClick) })
        }
    }
}

@Composable
private fun DashboardLayout(
    user: User,
    currentSection: DashboardSection,
    dashboardStats: DashboardStatsResponse?,
    isStatsLoading: Boolean,
    onEvent: (DashboardContract.Event) -> Unit
) {
    val sections = remember(user.role) { DashboardSection.getAvailableSections(user.role) }

    val uiItems = sections.map { section ->
        NavigationUiItem(
            id = section,
            title = stringResource(section.titleRes),
            icon = section.iconRes,
            isSelected = section == currentSection
        )
    }

    val fullName = "${user.firstName} ${user.lastName}"
    val initials = remember(fullName) {
        fullName.split(" ")
            .mapNotNull { it.firstOrNull()?.toString() }
            .take(2)
            .joinToString("")
            .uppercase()
    }

    Row(modifier = Modifier.fillMaxSize().background(White)) {

        SideNavigationPanel(
            items = uiItems,
            onItemSelected = { id ->
                onEvent(DashboardContract.Event.OnMenuSelected(id as DashboardSection))
            }
        )


        Column(modifier = Modifier.weight(1f)) {


            AdminPageHeader(
                title = stringResource(currentSection.titleRes),
                description = currentSection.getDescription(),
                userName = fullName,
                userRole = user.role.toUiString(),
                userInitials = initials
            )


            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(24.dp)
            ) {
                when (currentSection) {
                    DashboardSection.MODERATORS -> UsersScreen(
                        userRoles = listOf(UserRole.MODERATOR),
                        onNavigateToCreate = {},
                        onNavigateToEdit = {}
                    )
                    DashboardSection.DASHBOARD -> DashboardStatsSection(
                        role = user.role,
                        dashboardStats = dashboardStats,
                        isLoading = isStatsLoading
                    )
                    DashboardSection.VENUES -> VenuesScreen(
                        currentUser = user,
                        emptyStateImage = painterResource(Res.drawable.img_empty_ai_moderation),
                        modifier = Modifier.fillMaxSize(),
                        pickServicesPdfFile = { pickServicesPdfFileFromSystem() },
                        pickGalleryImageFile = { pickGalleryImageFromSystem() }
                    )
                    DashboardSection.SETTINGS -> Text("Налаштування")
                    DashboardSection.AI_MODERATION -> ReviewAiModerationScreen(
                        modifier = Modifier.fillMaxSize()
                    )
                    DashboardSection.USERS -> UsersScreen(
                        userRoles = listOf(UserRole.CLIENT, UserRole.BUSINESS_OWNER, UserRole.EMPLOYEE),
                        onNavigateToCreate = {},
                        onNavigateToEdit = {}
                    )

                    DashboardSection.REVIEWS -> OwnerReviewsScreen(
                        modifier = Modifier.fillMaxSize()
                    )

                    DashboardSection.TEAM ->  UsersScreen(
                        userRoles = listOf(UserRole.EMPLOYEE),
                        onNavigateToCreate = {},
                        onNavigateToEdit = {}
                    )
                }
            }
        }
    }
}

@Composable
fun ErrorScreen(onRetry: () -> Unit) {
    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        Column(horizontalAlignment = Alignment.CenterHorizontally) {
            Text("Не вдалося завантажити профіль")
            Spacer(modifier = Modifier.height(16.dp))
            Button(onClick = onRetry) {
                Text("Спробувати ще раз")
            }
        }
    }
}

private val DashboardSection.titleRes: StringResource
    get() = when (this) {
        DashboardSection.DASHBOARD -> Res.string.nav_dashboard
        DashboardSection.MODERATORS -> Res.string.nav_moderators
        DashboardSection.VENUES -> Res.string.nav_venues
        DashboardSection.AI_MODERATION -> Res.string.nav_ai_moderation
        DashboardSection.USERS -> Res.string.nav_users
        DashboardSection.SETTINGS -> Res.string.nav_settings
        DashboardSection.REVIEWS -> Res.string.nav_review
        DashboardSection.TEAM -> Res.string.nav_team
    }

@get:Composable
private val DashboardSection.iconRes: androidx.compose.ui.graphics.painter.Painter
    get() = when (this) {
        DashboardSection.DASHBOARD -> NavDashboard
        DashboardSection.MODERATORS -> NavModerators
        DashboardSection.VENUES -> NavBusiness
        DashboardSection.AI_MODERATION -> NavAiModeration
        DashboardSection.USERS -> NavUsers
        DashboardSection.SETTINGS -> NavSettings
        DashboardSection.REVIEWS -> NavReview
        DashboardSection.TEAM -> NavUsers
    }

@Composable
private fun DashboardSection.getDescription(): String {
    return when (this) {
        DashboardSection.MODERATORS -> "Тут ви створюєте свою команду."
        DashboardSection.DASHBOARD -> "Огляд ключових показників."
        DashboardSection.VENUES -> "Керування закладами."
        DashboardSection.AI_MODERATION -> "Модерація відгуків"
        DashboardSection.USERS -> "Робота з користувачами"
        DashboardSection.SETTINGS -> "Налаштування розділу."
        DashboardSection.REVIEWS -> "Робота з коментарями"
        DashboardSection.TEAM -> "Управління командою"
    }
}

private fun UserRole.toUiString(): String {
    return when (this) {
        UserRole.SUPER_ADMIN -> "Супер-адмін"
        UserRole.BUSINESS_OWNER -> "Власник бізнесу"
        UserRole.MODERATOR -> "Модератор"
        UserRole.EMPLOYEE -> "Працівник"
        UserRole.CLIENT -> "Клієнт"
    }
}

private suspend fun pickServicesPdfFileFromSystem(): VenuePickedFile? {
    return Promise<VenuePickedFile?> { resolve, _ ->
        val body = document.body
        if (body == null) {
            resolve(null)
            return@Promise
        }

        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "application/pdf,.pdf"
        input.style.display = "none"

        fun cleanup() {
            input.onchange = null
            input.remove()
        }

        input.onchange = changeHandler@{
            val file = input.files?.item(0)
            if (file == null) {
                cleanup()
                resolve(null)
                return@changeHandler null
            }

            val fileName = file.name
            val mimeType = resolveMimeType(file)
            val isPdf = mimeType == "application/pdf" || fileName.lowercase().endsWith(".pdf")
            if (!isPdf) {
                cleanup()
                resolve(null)
                return@changeHandler null
            }

            readFileBytes(file).then(
                onFulfilled = { bytes ->
                    cleanup()
                    resolve(
                        VenuePickedFile(
                            fileSource = KmpFileSource(
                                name = fileName,
                                bytes = bytes,
                                platformSource = file
                            ),
                            mimeType = "application/pdf",
                            fileName = fileName
                        )
                    )
                    null
                },
                onRejected = {
                    cleanup()
                    resolve(null)
                    null
                }
            )
            null
        }

        body.appendChild(input)
        input.click()
    }.await()
}

private suspend fun pickGalleryImageFromSystem(): VenuePickedFile? {
    return Promise<VenuePickedFile?> { resolve, _ ->
        val body = document.body
        if (body == null) {
            resolve(null)
            return@Promise
        }

        val input = document.createElement("input") as HTMLInputElement
        input.type = "file"
        input.accept = "image/*"
        input.style.display = "none"

        fun cleanup() {
            input.onchange = null
            input.remove()
        }

        input.onchange = changeHandler@{
            val file = input.files?.item(0)
            if (file == null) {
                cleanup()
                resolve(null)
                return@changeHandler null
            }

            val fileName = file.name
            val mimeType = resolveMimeType(file)
            val isImage = mimeType.startsWith("image/") ||
                fileName.lowercase().endsWith(".png") ||
                fileName.lowercase().endsWith(".jpg") ||
                fileName.lowercase().endsWith(".jpeg") ||
                fileName.lowercase().endsWith(".webp") ||
                fileName.lowercase().endsWith(".gif") ||
                fileName.lowercase().endsWith(".bmp") ||
                fileName.lowercase().endsWith(".svg")

            if (!isImage) {
                cleanup()
                resolve(null)
                return@changeHandler null
            }

            readFileBytes(file).then(
                onFulfilled = { bytes ->
                    cleanup()
                    resolve(
                        VenuePickedFile(
                            fileSource = KmpFileSource(
                                name = fileName,
                                bytes = bytes,
                                platformSource = file
                            ),
                            mimeType = mimeType,
                            fileName = fileName
                        )
                    )
                    null
                },
                onRejected = {
                    cleanup()
                    resolve(null)
                    null
                }
            )
            null
        }

        body.appendChild(input)
        input.click()
    }.await()
}

private fun readFileBytes(file: File): Promise<ByteArray> {
    val bufferPromise = file.asDynamic().arrayBuffer() as Promise<ArrayBuffer>
    return bufferPromise.then { buffer ->
        arrayBufferToByteArray(buffer)
    }
}

private fun arrayBufferToByteArray(buffer: ArrayBuffer): ByteArray {
    val bytes = Int8Array(buffer)
    return ByteArray(bytes.length) { index ->
        bytes.asDynamic()[index].unsafeCast<Byte>()
    }
}

private fun resolveMimeType(file: File): String {
    val directType = file.type
    if (directType.isNotBlank()) return directType

    val lowerName = file.name.lowercase()
    return when {
        lowerName.endsWith(".png") -> "image/png"
        lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg") -> "image/jpeg"
        lowerName.endsWith(".webp") -> "image/webp"
        lowerName.endsWith(".gif") -> "image/gif"
        lowerName.endsWith(".bmp") -> "image/bmp"
        lowerName.endsWith(".svg") -> "image/svg+xml"
        lowerName.endsWith(".pdf") -> "application/pdf"
        else -> "application/octet-stream"
    }
}
